import * as CryptoJS from 'crypto-js';
import BigInt from 'big-integer';
import { PrivateKey, Ecdsa } from 'starkbank-ecdsa';
import { secp256k1 } from 'starkbank-ecdsa/ellipticcurve/curve';
import type { PrivateKey as IPrivateKey, PublicKey as IPublicKey, Signature as ISignature } from 'starkbank-ecdsa';
import { bytesToHex } from './hex';

export async function derivePrivateKey(
  email: string,
  password: string,
  salt: string,
  iterations: number
): Promise<IPrivateKey> {
  // 1. Derive the secret using PBKDF2 from the CryptoJS namespace.
  // Convert salt from hex string to WordArray for CryptoJS
  const saltWordArray = CryptoJS.enc.Hex.parse(salt);
  
  const secretWordArray = CryptoJS.PBKDF2(`${email}:${password}`, saltWordArray, {
    keySize: 256 / 32, // keySize is in 32-bit words (256 bits)
    iterations: iterations,
    hasher: CryptoJS.algo.SHA256 // Use the hasher from the CryptoJS.algo namespace
  });

  // 2. Convert the derived secret to a hex string and then to BigInt.
  const secretHex = secretWordArray.toString(CryptoJS.enc.Hex);
  
  // 3. Convert to BigInt using big-integer library
  let secretBigInt = BigInt(secretHex, 16);

  // 4. Create an ECDSA private key from the secret with the built-in secp256k1 curve
  const privateKey = new PrivateKey(secp256k1, secretBigInt as any);
  
  return privateKey;
}

export function sign(privateKey: IPrivateKey, data: string): ISignature {
  // Use the Ecdsa.sign method from the starkbank-ecdsa library.
  return Ecdsa.sign(data, privateKey);
}

export function constructAuthMessage(userId: string, currentTime: string, body?: any): string {
  return constructRawAuthMessage(userId, currentTime, body ? JSON.stringify(body) : '');
}

/**
 * The same message, for a body that is already serialized.
 *
 * `constructAuthMessage` serializes its argument, which is right for a JSON body and
 * wrong for anything else: a multipart body is composed by the caller and must be
 * signed exactly as composed, never re-serialized. Both produce the identical
 * `{userId}:{currentTime}:{body}` shape the backend verifies against — they differ
 * only in who turned the body into a string.
 */
export function constructRawAuthMessage(userId: string, currentTime: string, rawBody: string): string {
  return `${userId}:${currentTime}:${rawBody}`;
}

export function getCurrentTimestamp(): string {
  return (Date.now() / 1000).toString();
}

export function verify(signature: ISignature, message: string, publicKey: IPublicKey): boolean {
  // Use the Ecdsa.verify method from the starkbank-ecdsa library.
  return Ecdsa.verify(message, signature, publicKey);
}

export function getPublicKey(privateKey: IPrivateKey): IPublicKey {
  // Derive the public key from the private key.
  return privateKey.publicKey();
}

export function generateSessionKeyPair(): { privateKey: IPrivateKey; publicKey: IPublicKey } {
  // Generate a new random private key for session using the built-in secp256k1 curve
  
  // Generate a random private key (d parameter) within the curve order
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  
  // Convert to BigInt and ensure it's within the curve order
  let d = BigInt(bytesToHex(randomBytes), 16);
  d = d.mod(secp256k1.N);
  
  // Ensure d is not zero
  if (d.equals(0)) {
    d = BigInt(1);
  }
  
  const privateKey = new PrivateKey(secp256k1, d as any);
  const publicKey = privateKey.publicKey();
  
  return { privateKey, publicKey };
}

/**
 * Converts an ECDSA signature to base64 format
 * Tries multiple methods with fallbacks for compatibility
 */
export function signatureToBase64(signature: ISignature): string {
  try {
    return signature.toBase64();
  } catch (e) {
    try {
      return signature.toDer();
    } catch (e2) {
      // Fallback: manually construct base64 from r and s values
      const rHex = signature.r.toString(16).padStart(64, '0');
      const sHex = signature.s.toString(16).padStart(64, '0');
      return btoa(rHex + sHex);
    }
  }
}

/**
 * Converts a private key to PEM format
 * Note: This is a simplified PEM format for ECDSA private keys
 */
export function privateKeyToPem(privateKey: IPrivateKey): string {
  try {
    // Try to use toPem if available
    if (typeof (privateKey as any).toPem === 'function') {
      return (privateKey as any).toPem();
    }
  } catch (e) {
    // Fall through to manual construction
  }
  
  // Manual PEM construction for ECDSA private key
  // Convert secret to hex string
  const secretHex = privateKey.secret.toString(16).padStart(64, '0');
  // Convert to base64
  const secretBase64 = btoa(secretHex.match(/.{1,2}/g)!.map((byte: string) => String.fromCharCode(parseInt(byte, 16))).join(''));
  // Format as PEM
  return `-----BEGIN EC PRIVATE KEY-----\n${secretBase64.match(/.{1,64}/g)?.join('\n') || secretBase64}\n-----END EC PRIVATE KEY-----`;
}

